import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { SideDrawer } from '../ui/SideDrawer';
import { Button } from '../ui/Button';
import { Plus, GripVertical, Copy, ExternalLink, Trash2, Search, Play, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { formatDateTime } from '../../lib/utils';
import { RuleSimulator } from './RuleSimulator';
import { SegmentBuilder } from './SegmentBuilder';
import type { OfferMetrics } from './LiveMetricsRow';
import type { SimRule } from '../../lib/rule-match';
import type { SegmentData } from './PriorityReorder';
import { ShareBar } from '../ui/charts/ShareBar';
import { OfferSparkline } from './OfferSparkline';
import type { OfferData } from './SegmentCard';

interface Campaign {
  id: number;
  slug: string;
  displayName: string;
  status: string;
  fallbackOfferId: number | null;
  botThreshold: number;
  rulesCount: number;
  createdAt: number;
}

export function RulesDrawer({
  campaign,
  offers,
  onClose,
}: {
  campaign: Campaign;
  offers: any[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [highlightedRuleId, setHighlightedRuleId] = useState<number | null>(null);
  const [showSimulatorStrip, setShowSimulatorStrip] = useState(false);
  const [showInlineAdd, setShowInlineAdd] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOptimizerPopover, setShowOptimizerPopover] = useState(false);

  const rules = useQuery({
    queryKey: ['rules', campaign.id],
    queryFn: () => apiClient.get<any[]>(`/api/v1/campaigns/${campaign.id}/rules`),
  });

  // Fetch by-offer stats (24h metrics) - single query, shared across all segments
  const offerStatsQuery = useQuery({
    queryKey: ['rules-by-offer', campaign.id],
    queryFn: async () => {
      const resp = await apiClient.get<{ data: OfferMetrics[] }>(
        `/api/v1/stats/by-offer?range=today&limit=200`,
      );
      return resp.data;
    },
    staleTime: 30_000,
  });

  const delRule = useMutation({
    mutationFn: (ruleId: number) => apiClient.delete(`/api/v1/campaigns/${campaign.id}/rules/${ruleId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rules', campaign.id] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  // ─── Drag Reorder Hook ──────────────────────────────────────────────
  interface DragState {
    draggedRuleId: number | null;
    sourceSegmentKey: string | null;
  }
  const [dragState, setDragState] = useState<DragState>({ draggedRuleId: null, sourceSegmentKey: null });

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, ruleId: number, segmentKey: string) => {
    setDragState({ draggedRuleId: ruleId, sourceSegmentKey: segmentKey });
    e.dataTransfer.effectAllowed = 'move';
    const img = document.createElement('div');
    img.style.cssText = 'width:1px;height:1px;opacity:0;position:absolute;top:-9999px;left:-9999px;';
    e.dataTransfer.setDragImage(img, 0, 0);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, targetRuleId: number, targetSegmentKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (dragState.draggedRuleId === null || !dragState.sourceSegmentKey) return;
    
    // Find positions in flat array of rules
    const allRules: Array<{ id: number; segmentKey: string }> = [];
    for (const seg of segments) {
      for (const rule of seg.rules) {
        allRules.push({ id: rule.id, segmentKey: seg.key });
      }
    }
    
    const draggedRule = allRules.find(r => r.id === dragState.draggedRuleId);
    const targetRule = allRules.find(r => r.id === targetRuleId);
    
    if (!draggedRule || !targetRule) return;
    
    const sourceIdx = allRules.findIndex(r => r.id === draggedRule.id);
    const targetIdx = allRules.findIndex(r => r.id === targetRule.id);
    
    if (sourceIdx === -1 || targetIdx === -1) return;
    if (sourceIdx === targetIdx) return;
    
    // Move rule in local state
    const newRules = [...allRules];
    const [moved] = newRules.splice(sourceIdx, 1);
    if (moved) newRules.splice(targetIdx, 0, moved);
    
    // Compute priority updates based on new order
    const updates = newRules.map((r, idx) => ({
      id: r.id,
      priority: idx * 10 + (idx % 10),
    }));
    
    // Submit reorder via API
    reorderRules.mutate({ updates });
    
    setDragState({ draggedRuleId: null, sourceSegmentKey: null });
  };

  const handleDragEnd = () => {
    setDragState({ draggedRuleId: null, sourceSegmentKey: null });
  };

  const reorderRules = useMutation({
    mutationFn: async ({ updates }: { updates: Array<{ id: number; priority: number }> }) => {
      return apiClient.post(`/api/v1/campaigns/${campaign.id}/rules/reorder`, { updates });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rules', campaign.id] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: () => {
      window.alert('Failed to save order. UI rolled back.');
    },
  });

  const rulesData = rules.data ?? [];
  
  const offerStatsMap = useMemo(() => {
    if (offerStatsQuery.isLoading) return new Map<number, OfferMetrics>();
    const map = new Map<number, OfferMetrics>();
    for (const m of offerStatsQuery.data ?? []) {
      map.set(m.offerId, m);
    }
    return map;
  }, [offerStatsQuery.data, offerStatsQuery.isLoading]);
  
  const activeRules = rulesData.filter((r) => r.isActive !== false);
  
  const simRules: SimRule[] = useMemo(() => {
    return activeRules.map((r) => ({
      id: r.id,
      country: r.country ?? null,
      deviceType: r.deviceType ?? null,
      os: r.os ?? null,
      source: r.source ?? null,
      weight: r.weight ?? 0,
      priority: r.priority ?? 100,
      isActive: true,
      scheduleActive: isScheduleActive(r.schedule),
      offerId: r.offerId,
    }));
  }, [activeRules]);

  function isScheduleActive(schedule: any): boolean | undefined {
    if (schedule == null) return true;
    const now = new Date();
    const dayOfWeek = now.getUTCDay();
    const dayBit = 1 << (dayOfWeek + 1);
    if (!(schedule.daysMask & dayBit)) return false;
    const currentHourUTC = now.getUTCHours();
    if (schedule.startHour < schedule.endHour) {
      return currentHourUTC >= schedule.startHour && currentHourUTC < schedule.endHour;
    } else {
      return currentHourUTC >= schedule.startHour || currentHourUTC < schedule.endHour;
    }
  }

  // ─── Group rules into segments ─────────────────────────────────────────
  const isWildcard = (v: string | null | undefined) => {
    const s = (v ?? '').toString().trim().toUpperCase();
    return !s || s === '*' || s === 'GLOBAL' || s === 'ALL' || s === 'ANY';
  };
  const segmentKey = (r: any) =>
    `${(r.country ?? '*').trim().toUpperCase()}|${(r.deviceType ?? '*').trim()}|${(r.os ?? '*').trim()}|${(r.source ?? '*').trim()}`;
  const segmentLabel = (r: any) => {
    const parts: string[] = [];
    if (!isWildcard(r.country)) parts.push(`country=${(r.country as string).toUpperCase()}`);
    if (!isWildcard(r.deviceType)) parts.push(`device=${r.deviceType}`);
    if (!isWildcard(r.os)) parts.push(`os=${r.os}`);
    if (!isWildcard(r.source)) parts.push(`source=${r.source}`);
    return parts.length === 0 ? 'Catch-all (any visitor)' : parts.join(' · ');
  };
  const specificity = (r: any) =>
    (isWildcard(r.country) ? 0 : 1) +
    (isWildcard(r.deviceType) ? 0 : 1) +
    (isWildcard(r.os) ? 0 : 1) +
    (isWildcard(r.source) ? 0 : 1);

  const segmentMap = new Map<string, any[]>();
  for (const r of activeRules) {
    const k = segmentKey(r);
    if (!segmentMap.has(k)) segmentMap.set(k, []);
    segmentMap.get(k)!.push(r);
  }
  const segments = [...segmentMap.entries()]
    .map(([key, rs]) => ({
      key,
      rules: rs,
      label: segmentLabel(rs[0]),
      specificity: specificity(rs[0]),
      priority: Math.min(...rs.map((r) => r.priority ?? 100)),
      totalWeight: rs.reduce((s, r) => s + Math.max(0, r.weight ?? 0), 0) || 1,
    }))
    .sort((a, b) =>
      a.priority !== b.priority ? a.priority - b.priority : b.specificity - a.specificity,
    );

  const segmentDataArray: SegmentData[] = segments;
  
  // Filter segments by search term
  const filteredSegments = useMemo(() => {
    if (!searchTerm.trim()) return segments;
    const term = searchTerm.toLowerCase();
    return segments.filter(seg => 
      seg.label.toLowerCase().includes(term) ||
      seg.rules.some(r => {
        const offer = offers.find(o => o.id === r.offerId);
        return offer?.name.toLowerCase().includes(term);
      })
    );
  }, [segments, searchTerm, offers]);

  // Calculate totals for status strip
  const totalSegments = segments.length;
  const totalRules = activeRules.length;
  const totalValidWeight = segments.reduce((sum, seg) => 
    sum + (seg.totalWeight === 100 ? 100 : 0), 0);

  const updateWeightMutation = useMutation({
    mutationFn: ({ ruleId, weight }: { ruleId: number; weight: number }) => 
      apiClient.patch(`/api/v1/campaigns/${campaign.id}/rules/${ruleId}`, {
        isActive: true,
        weight,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rules', campaign.id] });
    },
  });

  async function duplicateRule(r: any) {
    await apiClient.post(`/api/v1/campaigns/${campaign.id}/rules`, {
      offerId: r.offerId,
      country: r.country ?? '*',
      deviceType: r.deviceType ?? '*',
      os: r.os ?? '*',
      source: r.source ?? '*',
      weight: r.weight ?? 100,
      priority: r.priority ?? 100,
      ...(r.schedule ? { schedule: r.schedule } : {}),
    });
    qc.invalidateQueries({ queryKey: ['rules', campaign.id] });
  }

  async function openTestUrl() {
    try {
      const links = await apiClient.get<Array<{ domain: string }>>('/api/v1/tracking-links');
      if (!links?.length) {
        window.alert('No tracker domain configured');
        return;
      }
      window.open(`https://${links[0]!.domain}/${campaign.slug}?sub_id=_test`, '_blank');
    } catch {
      window.alert('Failed to load tracker domains');
    }
  }

  return (
    <SideDrawer
      open
      onClose={onClose}
      width="w-[66vw]"
      title="Routing Rules"
      subtitle={`${campaign.displayName} · #${campaign.id} · ${campaign.slug}`}
      badge={
        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-neon-green/10 text-neon-green border border-neon-green/30 text-[9px] font-mono uppercase shrink-0">
          LIVE
        </span>
      }
      actions={
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowSimulatorStrip(!showSimulatorStrip)}
          className="flex items-center gap-1.5 text-xs"
        >
          <Play size={12} />
          SIMULATE
        </Button>
      }
      footer={
        <div className="h-12 flex items-center justify-between px-4">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-mono text-text-secondary">
              <span className="text-neon-cyan">{totalRules}</span> aturan ·{' '}
              <span className="text-neon-magenta">{totalSegments}</span> segmen
            </span>
            <span
              className={`text-xs font-mono px-2 py-1 rounded border ${
                totalValidWeight === 100 * totalSegments
                  ? 'text-neon-green border-neon-green/30 bg-neon-green/5'
                  : 'text-neon-yellow border-neon-yellow/30 bg-neon-yellow/5'
              }`}
              title="Total bobot tiap segmen harus tepat 100% agar pembagian trafik akurat"
            >
              {totalValidWeight === 100 * totalSegments ? 'bobot valid ✓' : 'bobot ≠ 100%'}
            </span>
          </div>

          <div className="flex items-center gap-1.5 relative">
            <OptimizerChip
              campaignId={campaign.id}
              showPopover={showOptimizerPopover}
              onToggle={() => setShowOptimizerPopover(!showOptimizerPopover)}
            />
          </div>
        </div>
      }
      flush
    >
      <div className="flex flex-col h-full">
        {/* Simulator Strip */}
        {showSimulatorStrip && (
          <div className="shrink-0 border-b border-border-subtle bg-bg-primary">
            <RuleSimulator rules={simRules} offers={offers} />
          </div>
        )}

        {/* Toolbar */}
        <div className="h-10 flex items-center justify-between px-4 border-b border-border-subtle shrink-0 bg-bg-elevated">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-mono text-text-tertiary hidden sm:inline">
              <GripVertical size={13} className="inline mr-1" /> seret baris ⠿ = ubah prioritas
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Filter negara/offer..."
                className="pl-8 pr-3 py-2 w-56 h-9 text-xs font-mono bg-bg-primary border border-border-subtle rounded-sm focus:border-neon-cyan/50 focus:outline-none placeholder:text-text-quaternary"
              />
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowInlineAdd(!showInlineAdd)}
              className={`text-xs ${showInlineAdd ? 'text-neon-cyan' : ''}`}
            >
              <Plus size={12} className="inline mr-1" />
              NEW SEGMENT
            </Button>
          </div>
        </div>

        {/* DENSE TABLE SECTION */}
        <div className="flex-1 min-h-0 overflow-hidden bg-bg-primary">
          <div className="h-full overflow-y-auto p-3 space-y-1">
            {/* Table Header — align: PRI center, BOBOT center, OFFER kiri, METRIK kanan, AKSI kanan */}
            <div className="grid grid-cols-12 gap-3 px-3 py-3 bg-bg-elevated border border-border-subtle rounded-md mb-3 sticky top-0 z-10">
              <div className="col-span-1 text-[9px] font-mono uppercase text-text-secondary"></div>
              <div className="col-span-1 text-[11px] font-mono uppercase tracking-wider text-text-secondary text-center" title="Prioritas — angka kecil dievaluasi lebih dulu">PRI</div>
              <div className="col-span-2 text-[11px] font-mono uppercase tracking-wider text-text-secondary text-center" title="Bobot mentah; persentase = bobot ÷ total segmen">BOBOT</div>
              <div className="col-span-4 text-[11px] font-mono uppercase tracking-wider text-text-secondary">OFFER</div>
              <div className="col-span-2 text-[11px] font-mono uppercase tracking-wider text-text-secondary text-right" title="Klik hari ini · EPC (revenue ÷ klik berkualitas)">METRIK 24J</div>
              <div className="col-span-2 text-[11px] font-mono uppercase tracking-wider text-text-secondary text-right">AKSI</div>
            </div>

            {/* Segments */}
            {filteredSegments.length > 0 ? (
              filteredSegments.map((seg, segIdx) => (
                <SegmentGroup
                  key={seg.key}
                  segment={seg}
                  segmentIndex={segIdx}
                  offers={offers}
                  onDuplicate={duplicateRule}
                  onDelete={(ruleId) => delRule.mutate(ruleId)}
                  onTestUrl={openTestUrl}
                  byOfferStats={offerStatsMap}
                  campaignSlug={campaign.slug}
                  campaignId={campaign.id}
                  highlightedRuleId={highlightedRuleId}
                  onUpdateWeight={(ruleId, weight) => updateWeightMutation.mutate({ ruleId, weight })}
                  onDragRuleStart={handleDragStart}
                  onDragRuleOver={handleDragOver}
                />
              ))
            ) : (
              <div className="text-center py-8 text-text-tertiary text-xs font-mono">
                No matching segments{searchTerm ? ` for "${searchTerm}"` : ''}
              </div>
            )}

            {/* Inline Add — SegmentBuilder (form first-class: negara, device,
                offer splits, schedule) */}
            {showInlineAdd && (
              <div className="mx-2 mb-2">
                <SegmentBuilder
                  campaignId={campaign.id}
                  offers={offers}
                  existingRules={rulesData}
                  onAdded={() => {
                    qc.invalidateQueries({ queryKey: ['rules', campaign.id] });
                    qc.invalidateQueries({ queryKey: ['campaigns'] });
                    setShowInlineAdd(false);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </SideDrawer>
  );
}

// ─── Optimizer Chip Component ──────────────────────────────────────
function OptimizerChip({ 
  campaignId, 
  showPopover, 
  onToggle 
}: { 
  campaignId: number;
  showPopover: boolean;
  onToggle: () => void;
}) {
  const sl = useQuery({
    queryKey: ['smartlink', campaignId],
    queryFn: () => apiClient.get<{ isActive?: boolean }>(`/api/v1/campaigns/${campaignId}/smartlink`),
  });

  return (
    <>
      <button
        onClick={onToggle}
        className="flex items-center gap-2 text-[10px] font-mono text-text-tertiary hover:text-neon-cyan"
      >
        <Settings size={12} />
        Auto-Optimizer
        {sl.data?.isActive ? (
          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-neon-green/30 bg-neon-green/5">ACTIVE</span>
        ) : (
          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-border-subtle">OFF</span>
        )}
      </button>

      {showPopover && (
        <OptimizerPopover
          campaignId={campaignId}
          onClose={() => onToggle()}
        />
      )}
    </>
  );
}

// ─── Segment Group Component ──────────────────────────────────────────────
interface SegmentGroupProps {
  segment: {
    key: string;
    rules: Array<{
      id: number;
      offerId: number;
      weight: number;
      country?: string | null;
      deviceType?: string | null;
      os?: string | null;
      source?: string | null;
      priority?: number;
    }>;
    label: string;
    specificity: number;
    priority: number;
    totalWeight: number;
  };
  segmentIndex: number;
  offers: any[];
  onDuplicate: (rule: any) => void;
  onDelete: (ruleId: number) => void;
  onTestUrl: () => void;
  byOfferStats: Map<number, OfferMetrics>;
  campaignSlug: string;
  campaignId: number;
  highlightedRuleId: number | null;
  onUpdateWeight: (ruleId: number, weight: number) => void;
  onDragRuleStart: (e: React.DragEvent<HTMLDivElement>, ruleId: number, segmentKey: string) => void;
  onDragRuleOver: (e: React.DragEvent<HTMLDivElement>, targetRuleId: number, targetSegmentKey: string) => void;
}

function SegmentGroup({
  segment,
  segmentIndex,
  offers,
  onDuplicate,
  onDelete,
  onTestUrl,
  byOfferStats,
  onUpdateWeight,
  onDragRuleStart,
  onDragRuleOver,
  highlightedRuleId,
}: SegmentGroupProps) {
  const [collapsed, setCollapsed] = useState(false);
  const distributionColors = [
    'bg-neon-cyan',
    'bg-neon-magenta',
    'bg-neon-yellow',
    'bg-neon-green',
    'bg-neon-purple',
    'bg-neon-orange',
  ];

  const hasWildcardFull = segment.rules.length > 0 && segment.rules[0] && (() => {
    const fields = [segment.rules[0].country, segment.rules[0].deviceType, segment.rules[0].os, segment.rules[0].source];
    return fields.every((f) => {
      const s = (f ?? '').toString().trim().toUpperCase();
      return !s || s === '*' || s === 'GLOBAL' || s === 'ALL' || s === 'ANY';
    });
  })();

  const modeBadgeClass = hasWildcardFull
    ? 'text-neon-cyan border-neon-cyan/40 bg-neon-cyan/5'
    : 'text-neon-yellow border-neon-yellow/40 bg-neon-yellow/5';
  const modeLabel = hasWildcardFull ? 'Weighted pool' : 'Targeted segment';

  const shareBarSegments = segment.rules.map((r, i) => ({
    value: Math.max(0, r.weight ?? 0),
    className: distributionColors[i % distributionColors.length],
    title: `${offers.find((o) => o.id === r.offerId)?.name ?? '#' + r.offerId} — ${Math.round((Math.max(0, r.weight ?? 0) / segment.totalWeight) * 100)}%`,
  }));

  // Kondisi segmen = chips dari rule pertama (semua rule dalam satu segmen
  // punya kondisi identik — itu definisi pengelompokannya).
  const condChips = segment.rules[0]
    ? ([
        ['country', segment.rules[0].country, (v: string) => v.toUpperCase()],
        ['device', segment.rules[0].deviceType, (v: string) => v],
        ['os', segment.rules[0].os, (v: string) => v],
        ['source', segment.rules[0].source, (v: string) => v],
      ] as Array<[string, string | null | undefined, (v: string) => string]>)
        .filter(([, v]) => {
          const s = (v ?? '').toString().trim().toUpperCase();
          return s && s !== '*' && s !== 'GLOBAL' && s !== 'ALL' && s !== 'ANY';
        })
        .map(([k, v, fmt]) => (
          <span
            key={k}
            className="text-[11px] font-mono px-2 py-1 rounded border border-neon-cyan/40 bg-bg-elevated text-neon-cyan"
          >
            {k}={fmt(v as string)}
          </span>
        ))
    : [];

  return (
    <>
      {/* Segment Group Header — kondisi lengkap tampil DI SINI (sekali per
          segmen), bukan per baris, supaya tidak dobel dan mudah dibaca. */}
      <div
        className={`grid grid-cols-12 gap-3 px-3 py-3 rounded-md cursor-pointer border bg-surface-container-low border-border-subtle hover:border-neon-cyan/30 transition-colors`}
        onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? 'Klik untuk membuka segmen' : 'Klik untuk melipat segmen'}
      >
        <div className="col-span-1 flex items-center justify-center">
          {collapsed ? <ChevronDown size={16} className="text-text-tertiary" /> : <ChevronUp size={16} className="text-text-tertiary" />}
        </div>
        <div className="col-span-11 flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
          <span className="text-[11px] font-display uppercase tracking-wider text-text-secondary shrink-0">
            SEGMENT {segmentIndex + 1}
          </span>
          {condChips.length > 0 ? (
            <span className="flex flex-wrap items-center gap-1 min-w-0">{condChips}</span>
          ) : (
            <span className="text-xs font-mono text-text-primary">Catch-all (semua pengunjung)</span>
          )}
          <span
            className={`text-[11px] font-mono px-2 py-1 rounded border ${modeBadgeClass}`}
            title={hasWildcardFull
              ? 'Catch-all: menangani trafik yang tidak kena segmen mana pun'
              : 'Segment khusus: hanya pengunjung yang cocok dengan kondisi di atas'}
          >
            {hasWildcardFull ? 'Catch-all' : 'Targeted'}
          </span>
          <span
            className={`ml-auto text-xs font-mono px-2 py-1 rounded border shrink-0 ${
              segment.totalWeight === 100
                ? 'text-neon-green border-neon-green/30 bg-neon-green/5'
                : 'text-neon-red border-neon-red/30 bg-neon-red/5'
            }`}
            title="Total bobot segmen — harus 100% agar trafik terbagi penuh"
          >
            Σ {segment.totalWeight}%
          </span>
        </div>
      </div>

      {/* Share Bar for this segment */}
      {!collapsed && (
        <div className="mx-2 mb-1">
          <ShareBar segments={shareBarSegments} />
        </div>
      )}

      {!collapsed && segment.rules.map((rule) => {
        const offer = offers.find((o) => o.id === rule.offerId);
        const metrics = byOfferStats.get(rule.offerId) || null;
        const sharePct = Math.round((Math.max(0, rule.weight ?? 0) / segment.totalWeight) * 100);

        return (
          <div
            key={rule.id}
            className={`grid grid-cols-12 gap-3 px-3 py-3.5 rounded-md bg-bg-primary border border-border-subtle hover:border-neon-cyan/25 transition-colors ${
              highlightedRuleId === rule.id ? 'ring-1 ring-neon-magenta' : ''
            }`}
            draggable
            onDragStart={(e) => onDragRuleStart(e, rule.id, segment.key)}
            onDragOver={(e) => onDragRuleOver(e, rule.id, segment.key)}
          >
            {/* Drag Handle */}
            <div className="col-span-1 flex items-center justify-center" title="Seret untuk ubah urutan/prioritas">
              <GripVertical size={16} className="text-text-tertiary cursor-grab" />
            </div>

            {/* PRI — center agar sejajar header */}
            <div className="col-span-1 flex items-center justify-center">
              <span className="text-sm font-mono text-text-secondary numeric">{rule.priority}</span>
            </div>

            {/* Weight input — center */}
            <div className="col-span-2 flex items-center justify-center gap-1.5">
              <input
                type="number"
                min={0}
                max={100}
                value={rule.weight}
                onChange={(e) => onUpdateWeight(rule.id, Number(e.target.value))}
                title="Bobot mentah — persentase trafik = bobot ÷ total segmen"
                className="w-16 h-9 text-sm font-mono numeric bg-bg-elevated border border-border-subtle rounded-sm text-center focus:border-neon-cyan/50 focus:outline-none"
              />
              <span
                className={`text-xs font-mono numeric ${
                  sharePct === 0 ? 'text-text-quaternary' : 'text-text-secondary'
                }`}
                title="Pangsa trafik efektif dalam segmen ini"
              >
                {sharePct}%
              </span>
            </div>

            {/* Offer — nama + arah routing */}
            <div className="col-span-4 flex items-center min-w-0">
              <span className="text-sm font-mono text-neon-cyan truncate" title={offer?.name ?? `Offer #${rule.offerId}`}>
                → {offer?.name ?? `#${rule.offerId}`}
              </span>
            </div>

            {/* Metrics 24h — right align */}
            <div className="col-span-2 flex items-center justify-end gap-2 numeric">
              {metrics ? (
                <>
                  <span className="text-xs font-mono text-text-secondary numeric" title="Klik hari ini">
                    {metrics.clicksTotal} klik
                  </span>
                  <span className="text-xs font-mono text-neon-green numeric" title="EPC = revenue ÷ klik berkualitas">
                    ${((metrics.revenueGross / metrics.clicksQuality || 0)).toFixed(3)}
                  </span>
                </>
              ) : (
                <span className="text-xs font-mono text-text-quaternary">—</span>
              )}
            </div>

            {/* Actions — right align */}
            <div className="col-span-2 flex items-center justify-end gap-1">
              <button
                onClick={() => onDuplicate(rule)}
                className="text-text-tertiary hover:text-neon-cyan p-1.5"
                title="Duplikat rule"
              >
                <Copy size={15} />
              </button>
              <button
                onClick={onTestUrl}
                className="text-text-tertiary hover:text-neon-green p-1.5"
                title="Buka URL test (sub_id=_test)"
              >
                <ExternalLink size={15} />
              </button>
              <button
                onClick={() => onDelete(rule.id)}
                className="text-text-tertiary hover:text-neon-red p-1.5"
                title="Hapus rule"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
}

// ─── Optimizer Popover Component ──────────────────────────────────────────
interface OptimizerPopoverProps {
  campaignId: number;
  onClose: () => void;
}

function OptimizerPopover({ campaignId, onClose }: OptimizerPopoverProps) {
  const qc = useQueryClient();
  const sl = useQuery({
    queryKey: ['smartlink', campaignId],
    queryFn: () => apiClient.get<{ optimizationMetric?: string; lookbackWindowHours?: number; minClicksPerOffer?: number; explorationRate?: number; isActive?: boolean }>(`/api/v1/campaigns/${campaignId}/smartlink`),
  });

  const [form, setForm] = useState(sl.data ?? { optimizationMetric: 'epc', lookbackWindowHours: 24, minClicksPerOffer: 100, explorationRate: 10, isActive: false });
  const saving = useMutation({
    mutationFn: () => apiClient.put(`/api/v1/campaigns/${campaignId}/smartlink`, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['smartlink', campaignId] });
      onClose();
    },
  });

  if (sl.isLoading) return <div className="absolute right-0 top-full mt-2 p-3 bg-bg-elevated border border-neon-cyan/30 rounded shadow-lg text-[10px] font-mono">Loading...</div>;

  return (
    <div className="absolute right-0 top-full mt-2 w-80 p-4 bg-bg-elevated border border-neon-cyan/30 rounded shadow-lg z-50">
      <div className="text-[10px] font-display uppercase tracking-wider text-neon-cyan mb-3">Auto-Optimizer Config</div>
      
      <div className="space-y-2">
        <label className="block">
          <span className="text-[9px] font-mono text-text-tertiary uppercase">Metric</span>
          <select
            value={form.optimizationMetric}
            onChange={(e) => setForm({ ...form, optimizationMetric: e.target.value })}
            className="input-cyber h-7 text-[11px] w-full mt-1"
          >
            <option value="epc">EPC</option>
            <option value="cr">CR</option>
            <option value="revenue">Revenue</option>
          </select>
        </label>

        <label className="block">
          <span className="text-[9px] font-mono text-text-tertiary uppercase">Lookback (h)</span>
          <input
            type="number"
            min={24}
            max={720}
            step={24}
            value={form.lookbackWindowHours}
            onChange={(e) => setForm({ ...form, lookbackWindowHours: Number(e.target.value) })}
            className="input-cyber h-7 text-[11px] w-full mt-1"
          />
        </label>

        <label className="block">
          <span className="text-[9px] font-mono text-text-tertiary uppercase">Min clicks/offer</span>
          <input
            type="number"
            min={10}
            max={100000}
            value={form.minClicksPerOffer}
            onChange={(e) => setForm({ ...form, minClicksPerOffer: Number(e.target.value) })}
            className="input-cyber h-7 text-[11px] w-full mt-1"
          />
        </label>

        <label className="block">
          <span className="text-[9px] font-mono text-text-tertiary uppercase">Explore %</span>
          <input
            type="number"
            min={0}
            max={50}
            value={form.explorationRate}
            onChange={(e) => setForm({ ...form, explorationRate: Number(e.target.value) })}
            className="input-cyber h-7 text-[11px] w-full mt-1"
          />
        </label>

        <div className="flex items-center gap-2 pt-2 border-t border-border-subtle">
          <label className="flex items-center gap-2 text-[10px] font-mono text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              className="accent-neon-cyan"
            />
            Enabled
          </label>
          <button
            onClick={() => saving.mutate()}
            disabled={saving.isPending}
            className="ml-auto text-[10px] font-display uppercase tracking-wider px-3 py-1 bg-neon-cyan text-bg-primary rounded-sm disabled:opacity-40"
          >
            {saving.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

